import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { searchUsers, type PlayFabAccountInfo } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";

export function PlayersRoute() {
  const nav = useNavigate();
  const [value, setValue] = useState("");
  const [results, setResults] = useState<PlayFabAccountInfo[]>([]);
  const [searched, setSearched] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: () => searchUsers(value.trim()),
    onSuccess: (response) => {
      setResults(response.results);
      setWarnings(response.warnings);
      setSearched(true);
    },
    onError: () => {
      setResults([]);
      setWarnings([]);
      setSearched(false);
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    mutation.mutate();
  }

  return (
    <AppShell title="PlayFab (legacy)" subtitle="Search legacy PlayFab accounts by UID, display name, username, account email, or Google email">
      <form onSubmit={submit} className="max-w-3xl space-y-4">
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="UID / display name / username / account or Google email"
            autoFocus
          />
          <Button type="submit" disabled={!value.trim() || mutation.isPending}>
            {mutation.isPending ? "Searching…" : "Lookup"}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Display name is case-sensitive; email matching is case-insensitive. Google email uses imported or previously recorded legacy metadata and checks matches in PlayFab. An account without stored Google-email metadata may still require a UID lookup.
        </p>

        {mutation.error && (
          <Alert variant="destructive">
            <AlertDescription>{String(mutation.error)}</AlertDescription>
          </Alert>
        )}
        {warnings.map(warning => (
          <Alert key={warning}><AlertDescription>{warning}</AlertDescription></Alert>
        ))}
      </form>

      {searched && (
        <div className="max-w-3xl mt-6 space-y-2">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {results.length === 0 ? "No results" : `${results.length} result${results.length === 1 ? "" : "s"}`}
          </h2>

          {results.length > 0 && (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">UID</th>
                    <th className="px-3 py-2 text-left">Display name</th>
                    <th className="px-3 py-2 text-left">Username</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Google email</th>
                    <th className="px-3 py-2 text-left">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr
                      key={r.PlayFabId}
                      onClick={() => nav(`/players/${encodeURIComponent(r.PlayFabId)}`, { state: { origin: "playfab" } })}
                      className="border-t border-border/50 hover:bg-accent/40 cursor-pointer"
                    >
                      <td className="px-3 py-2 font-mono text-xs">{r.PlayFabId}</td>
                      <td className="px-3 py-2">{r.TitleInfo?.DisplayName ?? "—"}</td>
                      <td className="px-3 py-2">{r.Username ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">{r.PrivateInfo?.Email ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">{r.GoogleInfo?.GoogleEmail ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {formatTimestamp(r.TitleInfo?.Created)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
